import { useCallback } from "react";
import { useStorageSource } from "@rebasepro/app";
import { fileTarget } from "./_file_link";

export type PrivateFileOutcome = { ok: true } | { ok: false; message: string };

/** The slice of the storage source this needs. `fileNotFound` is how a 404 arrives. */
type SignedUrlSource = {
    getSignedUrl?: (key: string) => Promise<{ url?: string | null; fileNotFound?: boolean }>;
};

const capital = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

/**
 * Open a stored file — a CV — in a new tab, from a click.
 *
 * The column holds a storage key (see `_file_link.ts`), and a private object
 * has no address until one is asked for: `getSignedUrl` exchanges the key for a
 * short-lived, file-scoped download URL. A browser only lets a click open a tab
 * *synchronously*, and the URL arrives after an await — so the tab is opened
 * first, blank and cut off from this page (`opener = null`), and sent to the
 * file once the URL is there. The site's `openPrivateFile`
 * (src/common/rebase_client.ts) does the same for the employer.
 *
 * Returns what happened rather than reporting it, so each view says it in its
 * own snackbar. A 404 is its own outcome — the row names a file storage no
 * longer holds — because "gone" and "could not open it right now" are different
 * facts and only one of them is worth retrying.
 *
 * Admins may read any key (`storageAuthorize` in backend/src/index.ts), which is
 * who uses the panel.
 */
export function usePrivateFileOpener(): (value: string | null | undefined, noun: string) => Promise<PrivateFileOutcome> {
    const storage = useStorageSource() as unknown as SignedUrlSource | undefined;

    return useCallback(async (value: string | null | undefined, noun: string): Promise<PrivateFileOutcome> => {
        const target = fileTarget(value);
        if (!target) {
            return { ok: false, message: `No sabemos abrir ${noun}: la ficha guarda algo que no es ni un archivo nuestro ni un enlace.` };
        }

        if (target.kind === "link") {
            // A pasted link from before the column held keys. No await in
            // between, so the browser treats this as the click's own tab.
            window.open(target.href, "_blank", "noopener,noreferrer");
            return { ok: true };
        }

        // Before any await: after one, this is a popup and gets blocked.
        const tab = window.open("", "_blank");
        if (!tab) {
            return { ok: false, message: "El navegador ha bloqueado la pestaña nueva. Permite las ventanas emergentes para este panel y vuelve a intentarlo." };
        }
        tab.opener = null;

        try {
            if (!storage?.getSignedUrl) throw new Error("storage source unavailable");
            const signed = await storage.getSignedUrl(target.key);
            if (signed?.fileNotFound || !signed?.url) {
                tab.close();
                return { ok: false, message: `${capital(noun)} ya no está en nuestro almacenamiento: la ficha apunta a un archivo que no existe.` };
            }
            tab.location.href = signed.url;
            return { ok: true };
        } catch (err) {
            tab.close();
            console.error("No se pudo abrir el archivo", err);
            return { ok: false, message: `No pudimos abrir ${noun}. Vuelve a intentarlo en un momento.` };
        }
    }, [storage]);
}
